import { useCallback, useEffect, useMemo } from "react";
import {
  DATE_FORMATTER,
  MONTH_FORMATTER,
  formatCurrency,
  formatIsoDate,
  formatPercent,
  parseDateInput,
} from "../adminUtils";
import { formatDecimal } from "../../../utils/numberFormat";

const useAdminAnalytics = ({
  analyticsRangePreset,
  analyticsCustomFrom,
  analyticsCustomTo,
  analyticsCompareEnabled,
  analyticsCompareFrom,
  analyticsCompareTo,
  setAnalyticsCompareFrom,
  setAnalyticsCompareTo,
  analyticsLocationId,
  analyticsProductTypeId,
  analyticsGranularity,
  analyticsRankTab,
  analyticsReport,
  analyticsCompareReport,
  locations,
  dashboardCurrency,
}) => {
  const analyticsRange = useMemo(() => {
    const todayLocal = new Date();
    const todayUtc = new Date(Date.UTC(
      todayLocal.getUTCFullYear(),
      todayLocal.getUTCMonth(),
      todayLocal.getUTCDate()
    ));
    let start = todayUtc;
    let end = new Date(todayUtc.getTime() + 24 * 60 * 60 * 1000);
    let label = "Today";

    if (analyticsRangePreset === "thisMonth") {
      start = new Date(Date.UTC(todayUtc.getUTCFullYear(), todayUtc.getUTCMonth(), 1));
      end = new Date(Date.UTC(todayUtc.getUTCFullYear(), todayUtc.getUTCMonth() + 1, 1));
      label = `${MONTH_FORMATTER.format(start)} ${start.getUTCFullYear()}`;
    } else if (analyticsRangePreset === "last7Days") {
      start = new Date(todayUtc.getTime() - 6 * 24 * 60 * 60 * 1000);
      label = "Last 7 days";
    } else if (analyticsRangePreset === "last30Days") {
      start = new Date(todayUtc.getTime() - 29 * 24 * 60 * 60 * 1000);
      label = "Last 30 days";
    } else if (analyticsRangePreset === "custom") {
      const from = parseDateInput(analyticsCustomFrom);
      const to = parseDateInput(analyticsCustomTo);
      if (from && to && to >= from) {
        start = from;
        end = new Date(Date.UTC(
          to.getUTCFullYear(),
          to.getUTCMonth(),
          to.getUTCDate() + 1
        ));
        label = `${formatIsoDate(from)} → ${formatIsoDate(to)}`;
      }
    }

    const durationMs = Math.max(0, end.getTime() - start.getTime());
    const previousStart = new Date(start.getTime() - durationMs);
    const previousEnd = start;

    return {
      start,
      end,
      label,
      previousStart,
      previousEnd,
    };
  }, [analyticsCustomFrom, analyticsCustomTo, analyticsRangePreset]);

  const analyticsCompareRange = useMemo(() => {
    if (!analyticsCompareEnabled) return null;
    const from = parseDateInput(analyticsCompareFrom);
    const to = parseDateInput(analyticsCompareTo);
    if (!from || !to || to < from) return null;
    const end = new Date(Date.UTC(
      to.getUTCFullYear(),
      to.getUTCMonth(),
      to.getUTCDate() + 1
    ));
    return {
      start: from,
      end,
      label: `${formatIsoDate(from)} → ${formatIsoDate(to)}`,
    };
  }, [analyticsCompareEnabled, analyticsCompareFrom, analyticsCompareTo]);

  useEffect(() => {
    if (!analyticsCompareEnabled) return;
    const from = parseDateInput(analyticsCompareFrom);
    const to = parseDateInput(analyticsCompareTo);
    if (from && to && to >= from) return;
    const previousStart = analyticsRange.previousStart;
    const previousEnd = new Date(analyticsRange.previousEnd.getTime() - 24 * 60 * 60 * 1000);
    setAnalyticsCompareFrom(formatIsoDate(previousStart));
    setAnalyticsCompareTo(formatIsoDate(previousEnd));
  }, [
    analyticsCompareEnabled,
    analyticsCompareFrom,
    analyticsCompareTo,
    analyticsRange.previousEnd,
    analyticsRange.previousStart,
    setAnalyticsCompareFrom,
    setAnalyticsCompareTo,
  ]);

  const analyticsFilters = useMemo(
    () => ({
      locationId: analyticsLocationId !== "all" ? analyticsLocationId : undefined,
      equipmentTypeId:
        analyticsProductTypeId !== "all" ? analyticsProductTypeId : undefined,
      status: "all",
    }),
    [analyticsLocationId, analyticsProductTypeId]
  );

  const analyticsCurrency = analyticsReport?.currency || dashboardCurrency;
  const analyticsTotals = analyticsReport?.totals;
  const analyticsRates = analyticsReport?.rates;

  const analyticsSeries = useMemo(() => {
    if (!Array.isArray(analyticsReport?.series)) return [];
    return analyticsReport.series
      .map((point) => {
        const date = new Date(point.periodStartUtc);
        return {
          key: point.periodStartUtc,
          date,
          revenue: point.revenue ?? 0,
          refunds: point.refunds ?? 0,
          profit: point.profit ?? 0,
          bookings: point.bookingCount ?? 0,
        };
      })
      .sort((a, b) => a.date.getTime() - b.date.getTime());
  }, [analyticsReport]);

  const analyticsCompareSeries = useMemo(() => {
    if (!Array.isArray(analyticsCompareReport?.series)) return [];
    return analyticsCompareReport.series
      .map((point) => ({
        key: point.periodStartUtc,
        revenue: point.revenue ?? 0,
        refunds: point.refunds ?? 0,
        profit: point.profit ?? 0,
        bookings: point.bookingCount ?? 0,
      }))
      .sort((a, b) => new Date(a.key).getTime() - new Date(b.key).getTime());
  }, [analyticsCompareReport]);

  const analyticsCompareActive = Boolean(analyticsCompareReport);

  const analyticsStatusSeries = useMemo(() => {
    if (!Array.isArray(analyticsReport?.statusSeries)) return [];
    return analyticsReport.statusSeries
      .map((point) => ({
        key: point.periodStartUtc,
        completed: point.completedCount ?? 0,
        cancelled: point.cancelledCount ?? 0,
        refunded: point.refundedCount ?? 0,
      }))
      .sort((a, b) => new Date(a.key).getTime() - new Date(b.key).getTime());
  }, [analyticsReport]);

  const analyticsRangeLabel = analyticsRange.label;

  const formatAnalyticsLabel = useCallback(
    (date) => {
      if (!date) return "—";
      if (analyticsGranularity === "month") {
        return MONTH_FORMATTER.format(date);
      }
      if (analyticsGranularity === "week") {
        return `Wk of ${DATE_FORMATTER.format(date)}`;
      }
      return DATE_FORMATTER.format(date);
    },
    [analyticsGranularity]
  );

  const analyticsChartData = useMemo(() => {
    const compareMap = new Map(
      analyticsCompareSeries.map((entry) => [entry.key, entry])
    );
    return analyticsSeries.map((entry) => {
      const compare = compareMap.get(entry.key);
      return {
        key: entry.key,
        label: formatAnalyticsLabel(entry.date),
        gross: entry.revenue,
        net: entry.profit,
        refunds: entry.refunds * -1,
        bookings: entry.bookings,
        prevGross: compare?.revenue ?? null,
        prevNet: compare?.profit ?? null,
        prevBookings: compare?.bookings ?? null,
      };
    });
  }, [analyticsCompareSeries, analyticsSeries, formatAnalyticsLabel]);

  const analyticsBookingsChartData = useMemo(() => {
    const compareMap = new Map(
      analyticsCompareSeries.map((entry) => [entry.key, entry])
    );
    return analyticsStatusSeries.map((entry) => {
      const total = entry.completed + entry.cancelled + entry.refunded;
      const compare = compareMap.get(entry.key);
      return {
        key: entry.key,
        label: formatAnalyticsLabel(new Date(entry.key)),
        completed: entry.completed,
        cancelled: entry.cancelled,
        refunded: entry.refunded,
        total,
        prevTotal: compare?.bookings ?? null,
      };
    });
  }, [analyticsCompareSeries, analyticsStatusSeries, formatAnalyticsLabel]);

  const analyticsHasData = useMemo(
    () =>
      analyticsSeries.some(
        (entry) =>
          entry.revenue > 0 || entry.refunds > 0 || entry.profit > 0 || entry.bookings > 0
      ),
    [analyticsSeries]
  );

  const analyticsEquipmentUsage = useMemo(() => {
    return Array.isArray(analyticsReport?.equipmentUsage)
      ? analyticsReport.equipmentUsage
      : [];
  }, [analyticsReport]);

  const analyticsCompareEquipmentUsage = useMemo(() => {
    return Array.isArray(analyticsCompareReport?.equipmentUsage)
      ? analyticsCompareReport.equipmentUsage
      : [];
  }, [analyticsCompareReport]);

  const analyticsUsageByPeriod = useMemo(() => {
    const totals = new Map();
    analyticsEquipmentUsage.forEach((item) => {
      const key = item.periodStartUtc;
      const current = totals.get(key) || 0;
      totals.set(key, current + (item.usageCount ?? 0));
    });
    return analyticsSeries.map((entry) => ({
      key: entry.key,
      label: formatAnalyticsLabel(entry.date),
      usage: totals.get(entry.key) || 0,
    }));
  }, [analyticsEquipmentUsage, analyticsSeries, formatAnalyticsLabel]);

  const analyticsCompareUsageByPeriod = useMemo(() => {
    const totals = new Map();
    analyticsCompareEquipmentUsage.forEach((item) => {
      const key = item.periodStartUtc;
      const current = totals.get(key) || 0;
      totals.set(key, current + (item.usageCount ?? 0));
    });
    return analyticsSeries.map((entry) => ({
      key: entry.key,
      usage: totals.get(entry.key) || 0,
    }));
  }, [analyticsCompareEquipmentUsage, analyticsSeries]);

  const analyticsUsageChartData = useMemo(() => {
    const compareMap = new Map(
      analyticsCompareUsageByPeriod.map((entry) => [entry.key, entry])
    );
    return analyticsUsageByPeriod.map((entry) => ({
      ...entry,
      prevUsage: compareMap.get(entry.key)?.usage ?? null,
    }));
  }, [analyticsCompareUsageByPeriod, analyticsUsageByPeriod]);

  const analyticsUsageByEquipment = useMemo(() => {
    const totals = new Map();
    analyticsEquipmentUsage.forEach((item) => {
      const key = item.equipmentTypeName || "Equipment";
      const current = totals.get(key) || 0;
      totals.set(key, current + (item.usageCount ?? 0));
    });
    return Array.from(totals.entries())
      .map(([name, usage]) => ({ name, usage }))
      .sort((a, b) => b.usage - a.usage);
  }, [analyticsEquipmentUsage]);

  const analyticsLateReturns = useMemo(() => {
    if (!Array.isArray(analyticsReport?.lateReturns)) return [];
    return analyticsReport.lateReturns
      .map((point) => ({
        key: point.periodStartUtc,
        label: formatAnalyticsLabel(new Date(point.periodStartUtc)),
        count: point.count ?? 0,
      }))
      .sort((a, b) => new Date(a.key).getTime() - new Date(b.key).getTime());
  }, [analyticsReport, formatAnalyticsLabel]);

  const analyticsNoShows = useMemo(() => {
    if (!Array.isArray(analyticsReport?.noShows)) return [];
    return analyticsReport.noShows
      .map((point) => ({
        key: point.periodStartUtc,
        label: formatAnalyticsLabel(new Date(point.periodStartUtc)),
        count: point.count ?? 0,
      }))
      .sort((a, b) => new Date(a.key).getTime() - new Date(b.key).getTime());
  }, [analyticsReport, formatAnalyticsLabel]);

  const analyticsPickupDelay = useMemo(() => {
    if (!Array.isArray(analyticsReport?.pickupDelayMinutes)) return [];
    return analyticsReport.pickupDelayMinutes
      .map((point) => ({
        key: point.periodStartUtc,
        label: formatAnalyticsLabel(new Date(point.periodStartUtc)),
        value: point.value ?? 0,
      }))
      .sort((a, b) => new Date(a.key).getTime() - new Date(b.key).getTime());
  }, [analyticsReport, formatAnalyticsLabel]);

  const analyticsHeatmap = analyticsReport?.heatmap;
  const analyticsHeatmapSlots = useMemo(
    () => analyticsHeatmap?.slotLabels || [],
    [analyticsHeatmap]
  );
  const analyticsHeatmapCells = useMemo(
    () => analyticsHeatmap?.cells || [],
    [analyticsHeatmap]
  );
  const analyticsHeatmapMax = useMemo(() => {
    return analyticsHeatmapCells.reduce(
      (max, cell) => Math.max(max, cell.bookingCount ?? 0),
      0
    );
  }, [analyticsHeatmapCells]);

  const analyticsRefundReasons = useMemo(() => {
    return Array.isArray(analyticsReport?.refundReasons)
      ? analyticsReport.refundReasons
      : [];
  }, [analyticsReport]);

  const analyticsPartnerReliability = useMemo(() => {
    return Array.isArray(analyticsReport?.partnerReliability)
      ? analyticsReport.partnerReliability
      : [];
  }, [analyticsReport]);

  const analyticsAttendanceSeries = useMemo(() => {
    if (!Array.isArray(analyticsReport?.attendance?.series)) return [];
    return analyticsReport.attendance.series
      .map((point) => ({
        key: point.periodStartUtc,
        label: formatAnalyticsLabel(new Date(point.periodStartUtc)),
        pageVisits: point.pageVisits ?? 0,
        uniqueVisitors: point.uniqueVisitors ?? 0,
      }))
      .sort((a, b) => new Date(a.key).getTime() - new Date(b.key).getTime());
  }, [analyticsReport, formatAnalyticsLabel]);

  const analyticsAttendanceTotals = useMemo(() => {
    const attendance = analyticsReport?.attendance;
    const formatAttendancePath = (path) => {
      if (!path || path === "/") {
        return "Home (/)";
      }
      return path;
    };
    const raw = Array.isArray(attendance?.deviceBreakdown)
      ? attendance.deviceBreakdown
      : [];
    const rawMap = new Map(
      raw.map((item) => [
        item.deviceType || "Unknown",
        {
          count: item.count ?? 0,
          share: item.share ?? 0,
        },
      ])
    );

    const pcCount =
      (rawMap.get("Desktop")?.count ?? 0) + (rawMap.get("Unknown")?.count ?? 0);
    const phoneTabletCount =
      (rawMap.get("Mobile")?.count ?? 0) + (rawMap.get("Tablet")?.count ?? 0);
    const totalDeviceUsers = pcCount + phoneTabletCount;

    return {
      totalPageVisits: attendance?.totalPageVisits ?? 0,
      totalUniqueVisitors: attendance?.totalUniqueVisitors ?? 0,
      sourceBreakdown: Array.isArray(attendance?.sourceBreakdown)
        ? attendance.sourceBreakdown.map((item) => ({
            key: item.source || "Other",
            label: item.source || "Other",
            count: item.count ?? 0,
            share: item.share ?? 0,
          }))
        : [],
      sourceSeries: Array.isArray(attendance?.sourceSeries)
        ? attendance.sourceSeries
            .map((point) => ({
              key: point.periodStartUtc,
              label: formatAnalyticsLabel(new Date(point.periodStartUtc)),
              instagram: point.instagram ?? 0,
              tiktok: point.tikTok ?? point.tiktok ?? 0,
              other: point.other ?? 0,
            }))
            .sort((a, b) => new Date(a.key).getTime() - new Date(b.key).getTime())
        : [],
      deviceBreakdownRaw: Array.isArray(attendance?.deviceBreakdown)
        ? attendance.deviceBreakdown.map((item) => ({
            key: item.deviceType || "Unknown",
            label: item.deviceType || "Unknown",
            count: item.count ?? 0,
            share: item.share ?? 0,
          }))
        : [],
      deviceBreakdown: [
        {
          key: "pc",
          label: "PC users",
          count: pcCount,
          share:
            totalDeviceUsers > 0
              ? Math.round((pcCount / totalDeviceUsers) * 1000) / 10
              : 0,
        },
        {
          key: "phone_tablet",
          label: "Phone/Tablet users",
          count: phoneTabletCount,
          share:
            totalDeviceUsers > 0
              ? Math.round((phoneTabletCount / totalDeviceUsers) * 1000) / 10
              : 0,
        },
      ],
      topPages: Array.isArray(attendance?.topPages)
        ? attendance.topPages.map((item) => ({
            key: item.path || "/",
            path: item.path || "/",
            label: formatAttendancePath(item.path || "/"),
            visits: item.visits ?? 0,
            uniqueVisitors: item.uniqueVisitors ?? 0,
          }))
        : [],
      recentVisits: Array.isArray(attendance?.recentVisits)
        ? attendance.recentVisits.map((item, index) => ({
            key: `${item.occurredUtc || index}-${item.path || "/"}`,
            occurredUtc: item.occurredUtc,
            occurredLabel: item.occurredUtc
              ? new Date(item.occurredUtc).toLocaleString()
              : "—",
            path: item.path || "/",
            label: formatAttendancePath(item.path || "/"),
            deviceType: item.deviceType || "Unknown",
            source: item.source || "Other",
          }))
        : [],
    };
  }, [analyticsReport, formatAnalyticsLabel]);

  const analyticsFunnel = analyticsReport?.funnel;

  const buildSparklinePoints = useCallback((values) => {
    if (!values.length) return "";
    const min = Math.min(...values);
    const max = Math.max(...values);
    const range = max - min || 1;
    return values
      .map((value, index) => {
        const x = (index / Math.max(1, values.length - 1)) * 100;
        const y = 30 - ((value - min) / range) * 24;
        return `${x.toFixed(2)},${y.toFixed(2)}`;
      })
      .join(" ");
  }, []);

  const buildSparkline = useCallback(
    (values) => (
      <svg viewBox="0 0 100 32" className="admin-kpi__sparkline" aria-hidden="true">
        <polyline points={buildSparklinePoints(values)} />
      </svg>
    ),
    [buildSparklinePoints]
  );

  const analyticsKpis = useMemo(() => {
    if (!analyticsTotals) return [];
    const buildDelta = (current, previous) => {
      if (!Number.isFinite(previous) || previous === 0) {
        return { value: null, label: "—", tone: "muted" };
      }
      const delta = (current - previous) / Math.abs(previous);
      const label = `${delta >= 0 ? "+" : ""}${formatDecimal(delta * 100, 1)}%`;
      return {
        value: delta,
        label,
        tone: delta >= 0 ? "positive" : "negative",
      };
    };
    const previousTotals = analyticsCompareReport?.totals;
    const revenueDelta = buildDelta(
      analyticsTotals.revenue ?? 0,
      previousTotals?.revenue
    );
    const netDelta = buildDelta(
      analyticsTotals.profit ?? 0,
      previousTotals?.profit
    );
    const refundRate =
      analyticsTotals.revenue > 0
        ? analyticsTotals.refunds / analyticsTotals.revenue
        : 0;
    const refundRateDelta = buildDelta(
      refundRate,
      previousTotals?.revenue ? previousTotals.refunds / previousTotals.revenue : null
    );
    const bookingDelta = buildDelta(
      analyticsTotals.bookingCount ?? 0,
      previousTotals?.bookingCount
    );
    const avgValueDelta = buildDelta(
      analyticsTotals.avgBookingValue ?? 0,
      previousTotals?.avgBookingValue
    );
    const utilizationDelta = buildDelta(
      analyticsRates?.utilizationRate ?? 0,
      analyticsCompareReport?.rates?.utilizationRate
    );
    const onTimeDelta = buildDelta(
      analyticsRates?.onTimeReturnRate ?? 0,
      analyticsCompareReport?.rates?.onTimeReturnRate
    );
    const noShowDelta = buildDelta(
      analyticsRates?.noShowRate ?? 0,
      analyticsCompareReport?.rates?.noShowRate
    );

    return [
      {
        id: "kpi-gross",
        label: "Gross revenue",
        value: formatCurrency(analyticsTotals.revenue, analyticsCurrency),
        delta: revenueDelta,
        sparkline: analyticsSeries.map((item) => item.revenue),
      },
      {
        id: "kpi-net",
        label: "Net revenue",
        value: formatCurrency(analyticsTotals.profit, analyticsCurrency),
        delta: netDelta,
        sparkline: analyticsSeries.map((item) => item.profit),
      },
      {
        id: "kpi-refund",
        label: "Refund rate",
        value: formatPercent(refundRate),
        delta: refundRateDelta,
        sparkline: analyticsSeries.map((item) =>
          item.revenue ? item.refunds / item.revenue : 0
        ),
      },
      {
        id: "kpi-bookings",
        label: "Bookings count",
        value: analyticsTotals.bookingCount,
        delta: bookingDelta,
        sparkline: analyticsSeries.map((item) => item.bookings),
      },
      {
        id: "kpi-avg",
        label: "Avg booking value",
        value: formatCurrency(analyticsTotals.avgBookingValue, analyticsCurrency),
        delta: avgValueDelta,
        sparkline: analyticsSeries.map((item) =>
          item.bookings ? item.revenue / item.bookings : 0
        ),
      },
      {
        id: "kpi-utilization",
        label: "Utilization rate",
        value: formatPercent(analyticsRates?.utilizationRate ?? 0),
        delta: utilizationDelta,
        sparkline: analyticsUsageByPeriod.map((item) => item.usage),
      },
      {
        id: "kpi-on-time",
        label: "On-time return rate",
        value: formatPercent(analyticsRates?.onTimeReturnRate ?? 0),
        delta: onTimeDelta,
        sparkline: analyticsLateReturns.map((item) => item.count),
      },
      {
        id: "kpi-no-show",
        label: "No-show rate",
        value: formatPercent(analyticsRates?.noShowRate ?? 0),
        delta: noShowDelta,
        sparkline: analyticsNoShows.map((item) => item.count),
      },
    ];
  }, [
    analyticsCompareReport,
    analyticsCurrency,
    analyticsLateReturns,
    analyticsNoShows,
    analyticsRates,
    analyticsSeries,
    analyticsTotals,
    analyticsUsageByPeriod,
  ]);

  const analyticsLocationOptions = useMemo(
    () => [
      { value: "all", label: "All locations" },
      ...locations.map((location) => ({
        value: location.id,
        label: location.name,
      })),
    ],
    [locations]
  );

  const analyticsProductTypeOptions = useMemo(() => {
    const types = new Map();
    locations.forEach((location) => {
      location.inventory.forEach((item) => {
        if (!types.has(item.equipmentTypeId)) {
          types.set(item.equipmentTypeId, item.equipmentTypeName);
        }
      });
    });
    return [
      { value: "all", label: "All types" },
      ...Array.from(types.entries()).map(([id, name]) => ({
        value: id,
        label: name || "Equipment",
      })),
    ];
  }, [locations]);

  const analyticsHeatmapDays = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
  const analyticsHeatmapMap = useMemo(() => {
    const map = new Map();
    analyticsHeatmapCells.forEach((cell) => {
      map.set(`${cell.dayOfWeek}-${cell.slotLabel}`, cell.bookingCount ?? 0);
    });
    return map;
  }, [analyticsHeatmapCells]);

  const analyticsRankRows = useMemo(() => {
    if (!analyticsReport) return [];
    if (analyticsRankTab === "partner") {
      return analyticsReport.partnerPerformance || [];
    }
    return analyticsReport.topLocations || [];
  }, [analyticsRankTab, analyticsReport]);

  const analyticsRankMax = useMemo(() => {
    if (analyticsRankRows.length === 0) return 1;
    return Math.max(
      ...analyticsRankRows.map((row) => row.revenue ?? 0),
      1
    );
  }, [analyticsRankRows]);

  return {
    analyticsRange,
    analyticsCompareRange,
    analyticsFilters,
    analyticsCurrency,
    analyticsTotals,
    analyticsRates,
    analyticsSeries,
    analyticsCompareSeries,
    analyticsCompareActive,
    analyticsStatusSeries,
    analyticsRangeLabel,
    analyticsChartData,
    analyticsBookingsChartData,
    analyticsHasData,
    analyticsUsageChartData,
    analyticsUsageByEquipment,
    analyticsHeatmapSlots,
    analyticsHeatmapDays,
    analyticsHeatmapMap,
    analyticsHeatmapMax,
    analyticsAttendanceSeries,
    analyticsAttendanceTotals,
    analyticsFunnel,
    analyticsRefundReasons,
    analyticsPartnerReliability,
    analyticsLateReturns,
    analyticsNoShows,
    analyticsPickupDelay,
    analyticsKpis,
    analyticsLocationOptions,
    analyticsProductTypeOptions,
    analyticsRankRows,
    analyticsRankMax,
    buildSparkline,
  };
};

export default useAdminAnalytics;
